import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import './StorePopup.scss';

const POPUP_SESSION_PREFIX = 'store_home_popup_seen';

const canUseSessionStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    const testKey = `${POPUP_SESSION_PREFIX}:test`;
    window.sessionStorage.setItem(testKey, '1');
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

const StorePopup = ({ isHome, popup }) => {
  const [isVisible, setIsVisible] = useState(false);

  const sessionKey = useMemo(() => {
    if (!popup?.id || !popup?.image) return null;

    const host = typeof window !== 'undefined' ? window.location.host : 'store';
    return `${POPUP_SESSION_PREFIX}:${host}:${popup.id}`;
  }, [popup]);

  const closePopup = () => {
    if (sessionKey && canUseSessionStorage()) {
      window.sessionStorage.setItem(sessionKey, '1');
    }

    setIsVisible(false);
  };

  useEffect(() => {
    if (!isHome || !popup?.image || !sessionKey) {
      setIsVisible(false);
      return;
    }

    if (!canUseSessionStorage()) {
      setIsVisible(true);
      return;
    }

    if (window.sessionStorage.getItem(sessionKey)) {
      setIsVisible(false);
      return;
    }

    window.sessionStorage.setItem(sessionKey, '1');
    setIsVisible(true);
  }, [isHome, popup, sessionKey]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePopup();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  if (!isVisible || !popup?.image) {
    return null;
  }

  return (
    <div className="store-popup" role="dialog" aria-modal="true" aria-label={popup.name || 'Promocion'}>
      <button className="store-popup__backdrop" type="button" aria-label="Cerrar popup" onClick={closePopup} />
      <div className="store-popup__dialog">
        <button className="store-popup__close" type="button" aria-label="Cerrar popup" onClick={closePopup}>
          &times;
        </button>
        <img className="store-popup__image" src={popup.image} alt={popup.name || 'Promocion'} />
      </div>
    </div>
  );
};

StorePopup.defaultProps = {
  popup: null,
};

StorePopup.propTypes = {
  isHome: PropTypes.bool.isRequired,
  popup: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    image: PropTypes.string,
  }),
};

export default StorePopup;
